import React, { useEffect, useRef } from "react";
import "./AboutPage.css";
import Techstack from "../../techstack/Techstack";
import ToolsGrid from "../tools/ToolsGrid";
import "../tools/ToolsGrid.css";
import ExperienceWheel from "../experience/ExperienceWheel";

const FadeInSection = ({ children, delay = 0 }) => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("fade-in");
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div ref={sectionRef} className="item" style={{ transitionDelay: `${delay}s` }}>
      {children}
    </div>
  );
};

const AboutPage = () => {
  const experiences = [
    {
      company: "Northeastern IT",
      role: "UX Designer",
      time: "2024 – Present",
      summary: "Redesigning the student resource experience for 30k+ students.",
      color: "#6C8CFF",
      label: "NE"
    },
    {
      company: "Scout",
      role: "Developer",
      time: "Jan 2024 – Present",
      summary: "Building scroll-based interactions for a design conference site.",
      color: "#FF7AA2",
      label: "SC"
    },
    {
      company: "Priceline",
      role: "ML Developer Co-op",
      time: "Jan – Jun 2024",
      summary: "Prototyped GenAI hotel content workflows with Vertex AI.",
      color: "#FF9A3E",
      label: "PL"
    },
    {
      company: "re:Bloom",
      role: "Design Consultant",
      time: "May – Aug 2024",
      summary: "Shifted eCommerce experience from B2B to B2C for caregivers.",
      color: "#B28BFF",
      label: "RB"
    },
    {
      company: "Jefferies",
      role: "Technology Analyst",
      time: "Summer 2024",
      summary: "Explored corporate technology stacks and security workflows.",
      color: "#4FD1C5",
      label: "JF"
    },
    {
      company: "Tseng Lab",
      role: "Data Intern",
      time: "Sept 2023 – Aug 2024",
      summary: "Built expression datasets for gene discovery research.",
      color: "#8BD3E6",
      label: "TL"
    },
    {
      company: "Code4Community",
      role: "Director of Recruitment",
      time: "Sept 2023 – Present",
      summary: "Grew developer pipeline and supported Breaktime’s web app.",
      color: "#F85F73",
      label: "C4C"
    }
  ];

  return (
    <section className="about-page">
      {/* Decorative elements */}
      <div className="about-dots about-dots-1"></div>
      <div className="about-dots about-dots-2"></div>
      <div className="about-shape"></div>

      <div className="about-page-container">
        <FadeInSection>
          <h1 className="about-page-title">About Me</h1>
        </FadeInSection>
        
        {/* Intro - Most Important */}
        <FadeInSection delay={0.1}>
          <div className="about-intro">
            <p className="intro-large">
              Hi! I'm a <span className="highlight">computer science</span> student 
              at Northeastern University with a minor in <span className="highlight">Interaction Design</span>.
            </p>
            <p className="intro-large">
              I love working at the intersection of <span className="highlight">data</span>, 
              design, and development.
            </p>
          </div>
        </FadeInSection>

        {/* Experience - Rotating wheel */}
        <FadeInSection delay={0.2}>
          <ExperienceWheel experiences={experiences} />
        </FadeInSection>

        {/* Tools - Compact Section */}
        <FadeInSection delay={0.3}>
          <div className="about-tools-compact">
            <h2 className="about-subtitle">Tools I Use</h2>
            <ToolsGrid />
          </div>
        </FadeInSection>

        {/* Skills - temporarily hidden */}
        {/*
        <FadeInSection delay={0.4}>
          <div className="about-skills">
            <h2 className="about-subtitle">Skills</h2>
            <Techstack />
          </div>
        </FadeInSection>
        */}
      </div>
    </section>
  );
};

export default AboutPage;
